import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSwiper } from 'swiper/react'

import clsx from 'clsx'
import { Chip } from '../../adminPages/components/Chip'
import { LockIcon } from '../../adminPages/components/icons'
import { IMainCarouselItem } from '../../api/personas'
import { useMentorsState } from '../../hooks/storeHooks/useMentors'
import { MentorBase } from '../../types/mentor'
import { analytics } from '../../utils/analytics'
import { GradientButton } from '../GradientButton'

type IParallaxSlideProps = {
  slide: IMainCarouselItem
  withVideo: boolean
}

const Slide: FC<IParallaxSlideProps> = ({ slide, withVideo }) => {
  const { setSelectedMentor } = useMentorsState()
  const videoRef = useRef<HTMLVideoElement>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const [showVideo, setShowVideo] = useState(false)
  const [videoCanPlay, setVideoCanPlay] = useState(false)

  const swiper = useSwiper()

  // Memoize mentor data to avoid re-creating object on every render
  const mentorData = useMemo(
    () =>
      ({
        videoLoopUrl: slide.companion?.videoUrl,
        previewUrl: slide.companion?.imageUrl,
        jobTitle: slide.companion?.jobTitle,
        firstName: slide.companion?.firstName,
        lastName: slide.companion?.lastName,
        mentorId: slide.companion?.id,
      }) as MentorBase,
    [
      slide.companion?.videoUrl,
      slide.companion?.imageUrl,
      slide.companion?.jobTitle,
      slide.companion?.firstName,
      slide.companion?.lastName,
      slide.companion?.id,
    ],
  )

  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation()
      setSelectedMentor(mentorData)
    },
    [setSelectedMentor, mentorData],
  )

  const handleEnded = useCallback(() => {
    swiper.slideNext()
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [swiper])

  // Memoize video play function to prevent recreation
  const playVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current
        .play()
        .then(() => {
          if (videoRef.current) {
            videoRef.current.addEventListener('ended', handleEnded)
          }
        })
        .catch(console.log)
    }
  }, [handleEnded])

  // Memoize video cleanup function
  const cleanupVideo = useCallback(() => {
    if (videoRef.current) {
      try {
        videoRef.current.pause()
        videoRef.current.currentTime = 0
        videoRef.current.removeEventListener('ended', handleEnded)
      } catch (error: unknown) {
        // Ignore cleanup errors
        console.warn('Video cleanup warning:', error)
      }
    }
  }, [handleEnded])

  useEffect(() => {
    if (withVideo && videoCanPlay) {
      setShowVideo(true)
      return
    }
    setShowVideo(false)
  }, [withVideo, videoCanPlay])

  useEffect(() => {
    if (showVideo) {
      timerRef.current = setTimeout(() => {
        playVideo()
      }, 500)
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      cleanupVideo()
    }
  }, [showVideo, playVideo, cleanupVideo])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (!withVideo && videoRef.current) {
      setShowVideo(false)
      videoRef.current.pause()
      videoRef.current.currentTime = 0
      return
    }

    let isMounted = true
    let playAttempted = false

    const testVideoPlayability = async () => {
      if (playAttempted || !isMounted) return
      playAttempted = true

      try {
        await video.play()

        if (isMounted) {
          setVideoCanPlay(true)
          video.pause()
          video.currentTime = 0
        }
      } catch (error) {
        if (isMounted) {
          console.warn(
            'Autoplay blocked, but video element is functional:',
            error,
          )
          setVideoCanPlay(true)
        }
      }
    }

    void testVideoPlayability()

    // Cleanup function
    return () => {
      isMounted = false
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    }
  }, [withVideo])

  const handleSlideCardClick = () => {
    console.log('Slide clicked:', slide)
    const properties: Record<string, string> = {
      type: slide.sourceType,
      title: slide.title,
    }
    if (slide.sourceType === 'Companion' && slide.companion?.slug) {
      properties.companion_slug = slide.companion.slug
    }
    analytics.logEvent('carousel_item_clicked', properties)
  }

  const routePath =
    slide.sourceType === 'Banner'
      ? (slide.banner?.button?.url ?? '')
      : `/${slide.companion?.slug}?start=true${slide?.context?.id ? `&context=${slide.context?.id}` : ''}`
  const simpleRoutePath = `/${slide.companion?.slug}?start=true`
  const fullName = `${slide.companion?.firstName || ''} ${slide.companion?.lastName || ''}`

  return (
    <div
      className="relative w-full h-full flex flex-column overflow-hidden tc-mv-slide-container"
      onClick={handleSlideCardClick}
    >
      {/* Background */}
      <Link
        to={routePath}
        state={{ prevLocation: `${window.location.pathname}` }}
        className={`${slide.companion?.slug || slide.banner?.button?.url ? '' : 'pointer-events-none'}`}
      >
        <div className="tc-mv-slide-background rounded-regular">
          <img
            src={slide.backgroundImageUrl || undefined}
            alt={slide.title}
            className={`absolute w-full h-full object-cover ${
              showVideo ? 'opacity-0' : 'opacity-100'
            } transition-opacity duration-[2000ms]`}
          />

          {slide.backgroundVideoUrl && (
            <video
              ref={videoRef}
              preload="metadata"
              src={slide.backgroundVideoUrl}
              muted
              autoPlay
              loop
              playsInline
              controlsList="nodownload"
              disablePictureInPicture
              className={`absolute w-full h-full object-cover ${
                showVideo ? 'opacity-100' : 'opacity-0'
              } transition-opacity duration-[2000ms]`}
            />
          )}
        </div>
        <div>
          {slide.visibility === 'admin' && (
            <div className="tc-mv-slide-admin-badge">
              <Chip
                icon={<LockIcon />}
                color="secondary"
                label="Admins only"
                className="hover:!bg-purple-500"
              />
            </div>
          )}
        </div>
      </Link>
      {/* Content */}
      <div className="tc-mv-slide-content">
        {slide.companion && (
          <div className="tc-mv-mentor-video-container mt-2">
            <Link
              to={simpleRoutePath}
              className={`block w-full h-full ${!slide.companion?.slug ? 'pointer-events-none' : ''}`}
              onClick={handleCardClick}
            >
              <video
                preload="metadata"
                muted
                autoPlay
                loop
                playsInline
                controlsList="nodownload"
                disablePictureInPicture
                className="w-full h-full object-cover"
                poster={slide.companion?.imageUrl}
                src={slide.companion?.videoUrl || undefined}
              />
            </Link>
          </div>
        )}
        <div className="flex-1 space-y-3 text-start">
          <div className="tc-mv-mentor-info !mt-0">
            {slide.sourceType !== 'Banner' && (
              <Link
                to={simpleRoutePath}
                onClick={handleCardClick}
                className={`${!slide.companion?.slug ? 'pointer-events-none' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <div className="tc-mv-mentor-video-container !block min-950:!hidden">
                    <video
                      preload="metadata"
                      muted
                      autoPlay
                      loop
                      playsInline
                      controlsList="nodownload"
                      disablePictureInPicture
                      className="w-full h-full object-cover"
                      poster={slide.companion?.imageUrl}
                      src={slide.companion?.videoUrl || undefined}
                    />
                  </div>
                  <div className="flex items-start min-950:items-center flex-col min-950:flex-row gap-1 min-950:gap-0 [text-shadow:1px_1px_4px_#000] min-950:[text-shadow:none]">
                    {fullName}
                    {!slide.companion?.hideJobTitle &&
                    slide.companion?.jobTitle ? (
                      <span className="min-950:text-[rgba(255,255,255,0.80)]">
                        <span className="hidden min-950:inline">,&nbsp;</span>
                        {slide.companion?.jobTitle}
                      </span>
                    ) : null}
                  </div>
                </div>
              </Link>
            )}
          </div>
          <div className="tc-mv-slide-title line-clamp-2 max-w-[670px] min-950:!mt-1">
            {slide.title}
          </div>
          <div className="tc-mv-mentor-info space-y-3">
            {slide.description && (
              <div
                className={clsx('max-w-[640px]', {
                  'hidden min-950:block': slide.sourceType !== 'Banner',
                })}
              >
                <div
                  title={slide.description}
                  className={clsx('text-sm line-clamp-1', {
                    '!line-clamp-2': slide.sourceType === 'Banner',
                  })}
                >
                  {slide.description}
                </div>
              </div>
            )}
            <Link className="block !mt-4" target="_blank" to={routePath}>
              {slide.sourceType !== 'Banner' ? (
                <GradientButton>
                  {slide.sourceType === 'Companion' ? 'Chat now' : 'Watch now'}
                </GradientButton>
              ) : (
                <GradientButton>
                  {slide.banner?.button?.label || 'Learn more'}
                </GradientButton>
              )}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export { Slide }
